// src/pages/harvesting/monitor-hasil-panen/LokasiClosingByMouth.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Stack,
    Chip,
    Menu,
    MenuItem,
    Grid,
    IconButton,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import CustomTableContainer
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find((lokasi) => lokasi.id_lokasi === id_lokasi);
};

// Helper: Parse tanggal dengan berbagai format
const parseTanggalClosing = (tanggalStr) => {
    if (!tanggalStr || tanggalStr === "-") return null;

    try {
        let day, month, year;

        // Cek format: DD-MM-YYYY
        if (tanggalStr.includes("-")) {
            const parts = tanggalStr.split("-");
            if (parts.length === 3) {
                day = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10);
                year = parseInt(parts[2], 10);
            }
        }
        // Cek format: DD/MM/YYYY
        else if (tanggalStr.includes("/")) {
            const parts = tanggalStr.split("/");
            if (parts.length === 3) {
                day = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10);
                year = parseInt(parts[2], 10);
            }
        }

        // Validasi
        if (day && month && year && year >= 2000 && year <= 2100) {
            return { day, month, year };
        }
    } catch (error) {
        console.error("Error parsing tanggal:", tanggalStr, error);
    }

    return null;
};

// Helper: Konversi string angka dengan koma ke number
const parseNumberWithComma = (str) => {
    if (!str || str === "-" || str === "") return 0;
    try {
        const num = parseFloat(String(str).replace(",", "."));
        return isNaN(num) ? 0 : num;
    } catch {
        return 0;
    }
};

// Daftar nama bulan dalam Bahasa Indonesia
const bulanList = [
    { id: 1, nama: "Januari" },
    { id: 2, nama: "Februari" },
    { id: 3, nama: "Maret" },
    { id: 4, nama: "April" },
    { id: 5, nama: "Mei" },
    { id: 6, nama: "Juni" },
    { id: 7, nama: "Juli" },
    { id: 8, nama: "Agustus" },
    { id: 9, nama: "September" },
    { id: 10, nama: "Oktober" },
    { id: 11, nama: "November" },
    { id: 12, nama: "Desember" },
];

function LokasiClosingByMouth() {
    const [dataList, setDataList] = useState([]);
    const [allDataList, setAllDataList] = useState([]); // Simpan semua data untuk filtering
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0); // 0 = Chart, 1 = Tabel

    // State untuk filter bulan dan tahun
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Default bulan saat ini
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Default tahun saat ini

    // State untuk menu dropdown
    const [monthAnchorEl, setMonthAnchorEl] = useState(null);
    const [yearAnchorEl, setYearAnchorEl] = useState(null);

    // State untuk data lokasi tambahan
    const [lokasiData, setLokasiData] = useState([]);

    // Generate daftar tahun (dari 2020 sampai tahun depan)
    const currentYear = new Date().getFullYear();
    const yearList = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i).filter((year) => year >= 2020);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Handler untuk bulan dropdown
    const handleMonthClick = (event) => {
        setMonthAnchorEl(event.currentTarget);
    };

    const handleMonthClose = () => {
        setMonthAnchorEl(null);
    };

    const handleMonthSelect = (monthId) => {
        setSelectedMonth(monthId);
        handleMonthClose();
    };

    // Handler untuk tahun dropdown
    const handleYearClick = (event) => {
        setYearAnchorEl(event.currentTarget);
    };

    const handleYearClose = () => {
        setYearAnchorEl(null);
    };

    const handleYearSelect = (year) => {
        setSelectedYear(year);
        handleYearClose();
    };

    // Filter data berdasarkan bulan dan tahun yang dipilih
    useEffect(() => {
        if (allDataList.length > 0) {
            const filteredData = allDataList.filter((item) => {
                if (!item.tanggal_parsed) return false;
                return item.tanggal_parsed.month === selectedMonth && item.tanggal_parsed.year === selectedYear;
            });

            // Sort berdasarkan tanggal (terbaru dulu)
            filteredData.sort((a, b) => {
                try {
                    const dateA = new Date(a.tanggal_parsed.year, a.tanggal_parsed.month - 1, a.tanggal_parsed.day);
                    const dateB = new Date(b.tanggal_parsed.year, b.tanggal_parsed.month - 1, b.tanggal_parsed.day);
                    return dateB - dateA; // Descending (terbaru dulu)
                } catch (e) {
                    return 0;
                }
            });

            setDataList(filteredData);
        }
    }, [selectedMonth, selectedYear, allDataList]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Fetch data monitoring hasil panen
                const monitorRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
                const monitorSnapshot = await onValuePromise(monitorRef);
                const monitorData = monitorSnapshot.val();

                if (!monitorData) {
                    setDataList([]);
                    setAllDataList([]);
                    setLoading(false);
                    return;
                }

                // 2. Fetch data lokasi untuk mendapatkan informasi tambahan
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();

                let lokasiArray = [];
                if (lokasiRawData) {
                    lokasiArray = Object.keys(lokasiRawData).map((key) => ({
                        firebaseKey: key,
                        ...lokasiRawData[key],
                    }));
                    setLokasiData(lokasiArray);
                }

                // 3. Process semua data
                const allProcessedData = [];
                const processedData = [];

                Object.keys(monitorData).forEach((key) => {
                    const record = monitorData[key];

                    // Cek tanggal closing
                    const tanggalClosing = record.tanggal_closing;
                    if (!tanggalClosing || tanggalClosing === "-") {
                        return;
                    }

                    // Parse tanggal
                    const parsedDate = parseTanggalClosing(tanggalClosing);
                    if (!parsedDate) return;

                    // Cari info lokasi tambahan
                    const lokasiInfo = findLokasiById(lokasiArray, record.id_lokasi);

                    // Format tanggal untuk display (gunakan format asli)
                    const displayDate = tanggalClosing;

                    const dataItem = {
                        firebaseKey: key,
                        id_lokasi: record.id_lokasi || "",
                        lokasi: lokasiInfo?.lokasi || record.id_lokasi || "-",
                        status: lokasiInfo?.status || "-", // Ambil status dari data lokasi
                        tanggal_closing: displayDate,
                        tanggal_parsed: parsedDate,
                        target_panen: record.target_panen || "-",
                        real_panen: record.real_panen || "-",
                        luas: lokasiInfo?.netto || "-",
                        rencana_yield: "-",
                        yield: "-",
                    };

                    // Hitung Rencana Yield dan Yield
                    try {
                        // Rencana Yield = Target Panen / Luas
                        const target = parseNumberWithComma(dataItem.target_panen);
                        const luasValue = parseNumberWithComma(dataItem.luas);

                        if (target !== 0 && luasValue !== 0) {
                            dataItem.rencana_yield = (target / luasValue).toFixed(2).replace(".", ",");
                        }

                        // Yield = Real Panen / Luas
                        const real = parseNumberWithComma(dataItem.real_panen);

                        if (real !== 0 && luasValue !== 0) {
                            dataItem.yield = (real / luasValue).toFixed(2).replace(".", ",");
                        }
                    } catch (calcError) {
                        console.error("Error calculating yield:", calcError);
                    }

                    // Simpan semua data
                    allProcessedData.push(dataItem);

                    // Filter untuk bulan dan tahun default (saat ini)
                    if (parsedDate.month === selectedMonth && parsedDate.year === selectedYear) {
                        processedData.push(dataItem);
                    }
                });

                // Sort data default (bulan dan tahun saat ini)
                processedData.sort((a, b) => {
                    try {
                        const dateA = new Date(a.tanggal_parsed.year, a.tanggal_parsed.month - 1, a.tanggal_parsed.day);
                        const dateB = new Date(b.tanggal_parsed.year, b.tanggal_parsed.month - 1, b.tanggal_parsed.day);
                        return dateB - dateA; // Descending (terbaru dulu)
                    } catch (e) {
                        return 0;
                    }
                });

                // Sort all data
                allProcessedData.sort((a, b) => {
                    try {
                        const dateA = new Date(a.tanggal_parsed.year, a.tanggal_parsed.month - 1, a.tanggal_parsed.day);
                        const dateB = new Date(b.tanggal_parsed.year, b.tanggal_parsed.month - 1, b.tanggal_parsed.day);
                        return dateB - dateA;
                    } catch (e) {
                        return 0;
                    }
                });

                setDataList(processedData);
                setAllDataList(allProcessedData);
                setError(null);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Gagal memuat data lokasi closing");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Filter data berdasarkan status lokasi
    const filterDataByStatus = (status) => {
        return dataList.filter((item) => item.status === status);
    };

    // Data untuk chart NFFC
    const nffcData = filterDataByStatus("NFFC");
    const chartDataNFFC = {
        labels: nffcData.map((item) => item.lokasi),
        rencanaYieldData: nffcData.map((item) => parseNumberWithComma(item.rencana_yield)),
        yieldData: nffcData.map((item) => parseNumberWithComma(item.yield)),
    };

    // Data untuk chart NFSC
    const nfscData = filterDataByStatus("NFSC");
    const chartDataNFSC = {
        labels: nfscData.map((item) => item.lokasi),
        rencanaYieldData: nfscData.map((item) => parseNumberWithComma(item.rencana_yield)),
        yieldData: nfscData.map((item) => parseNumberWithComma(item.yield)),
    };

    // Data untuk chart utama (semua data)
    const chartData = {
        labels: dataList.map((item) => item.lokasi),
        rencanaYieldData: dataList.map((item) => parseNumberWithComma(item.rencana_yield)),
        yieldData: dataList.map((item) => parseNumberWithComma(item.yield)),
    };

    // Dapatkan nama bulan yang dipilih
    const selectedMonthName = bulanList.find((bulan) => bulan.id === selectedMonth)?.nama || "";

    // Fungsi untuk merender barchart
    const renderBarChart = (chartData, title) => {
        if (chartData.labels.length === 0) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Tidak ada data untuk {title}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ height: "100%", width: "100%", p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, textAlign: "center" }}>
                    {title}
                </Typography>
                <BarChart
                    xAxis={[
                        {
                            data: chartData.labels,
                            scaleType: "band",
                            label: "Lokasi",
                            disableTicks: false,
                            padding: { left: 0, right: 0 },
                        },
                    ]}
                    yAxis={[
                        {
                            min: 0,
                            label: "",
                            disableLine: false,
                            disableTicks: false,
                        },
                    ]}
                    series={[
                        {
                            data: chartData.rencanaYieldData,
                            label: "Rencana Yield (Ton/Ha)",
                            color: "#fdd835",
                        },
                        {
                            data: chartData.yieldData,
                            label: "Yield Aktual (Ton/Ha)",
                            color: "#4caf50",
                        },
                    ]}
                    margin={{ top: 40, right: 10, bottom: 20, left: 0 }}
                    slotProps={{
                        legend: {
                            direction: "row",
                            position: { vertical: "top", horizontal: "right" },
                            padding: 0,
                            itemMarkWidth: 8,
                            itemMarkHeight: 8,
                            labelStyle: { fontSize: 11 },
                            itemGap: 8,
                        },
                    }}
                    height={280}
                    disableClipPath={true}
                    sx={{
                        "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                            stroke: "#666666",
                        },
                        "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                            stroke: "#666666",
                        },
                        "& .MuiChartsAxis-left .MuiChartsAxis-label": {
                            display: "none",
                        },
                    }}
                />
            </Box>
        );
    };

    if (loading) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 2, mb: 3 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                    Lokasi Closing Bulan Ini
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        Memuat data...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 2, mb: 3 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                    Lokasi Closing
                </Typography>
                <Box sx={{ p: 2 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </Paper>
        );
    }

    if (dataList.length === 0) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Lokasi Closing {selectedMonthName} {selectedYear}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Chip Bulan dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleMonthClick}
                            label={selectedMonthName}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: monthAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleMonthClick}
                            sx={{ cursor: "pointer" }}
                        />

                        {/* Chip Tahun dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleYearClick}
                            label={selectedYear.toString()}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: yearAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleYearClick}
                            sx={{ cursor: "pointer" }}
                        />

                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ cursor: "pointer" }}
                            label={`${dataList.length} lokasi`}
                        />
                    </Stack>
                </Stack>

                {/* Tab Navigation */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Tabel"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 100,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        p: 0,
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        Tidak ada data closing untuk {selectedMonthName} {selectedYear}.
                    </Typography>
                </Box>

                {/* Menu Dropdown Bulan */}
                <Menu
                    anchorEl={monthAnchorEl}
                    open={Boolean(monthAnchorEl)}
                    onClose={handleMonthClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {bulanList.map((bulan) => (
                        <MenuItem
                            key={bulan.id}
                            onClick={() => handleMonthSelect(bulan.id)}
                            selected={selectedMonth === bulan.id}
                        >
                            {bulan.nama}
                        </MenuItem>
                    ))}
                </Menu>

                {/* Menu Dropdown Tahun */}
                <Menu
                    anchorEl={yearAnchorEl}
                    open={Boolean(yearAnchorEl)}
                    onClose={handleYearClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {yearList.map((year) => (
                        <MenuItem key={year} onClick={() => handleYearSelect(year)} selected={selectedYear === year}>
                            {year}
                        </MenuItem>
                    ))}
                </Menu>
            </Paper>
        );
    }

    return (
        <Box>
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Lokasi Closing {selectedMonthName} {selectedYear}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Chip Bulan dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleMonthClick}
                            label={selectedMonthName}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: monthAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleMonthClick}
                            sx={{ cursor: "pointer" }}
                        />

                        {/* Chip Tahun dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleYearClick}
                            label={selectedYear.toString()}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: yearAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleYearClick}
                            sx={{ cursor: "pointer" }}
                        />

                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ cursor: "pointer" }}
                            label={`${dataList.length} lokasi`}
                        />
                    </Stack>
                </Stack>

                {/* Tab Navigation */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Tabel"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                {/* Container dengan tinggi tetap untuk kedua tab */}
                <Box>
                    {/* Chart Tab Content */}
                    {activeTab === 0 && (
                        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                            <Grid size={6}>
                                <Box
                                    sx={{
                                        height: 350,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Barchart NFFC  */}
                                    {renderBarChart(chartDataNFFC, "Lokasi NFFC")}
                                </Box>
                            </Grid>
                            <Grid size={6}>
                                <Box
                                    sx={{
                                        height: 350,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Barchart NFSC */}
                                    {renderBarChart(chartDataNFSC, "Lokasi NFSC")}
                                </Box>
                            </Grid>
                            <Grid size={12}>
                                <Box
                                    sx={{
                                        height: 350,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        overflow: "hidden",
                                    }}
                                >
                                    {renderBarChart(chartData, "Semua Lokasi")}
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {/* Table Tab Content */}
                    {activeTab === 1 && (
                        <Box
                            sx={{
                                height: 350,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                bgcolor: "background.paper",
                                overflow: "hidden",
                            }}
                        >
                            <Box sx={{ height: "100%", width: "100%" }}>
                                {/* Gunakan CustomTableContainer di sini */}
                                <CustomTableContainer
                                    sx={{
                                        height: "100%", // Isi penuh container
                                        border: "none", // Hapus border karena sudah ada di parent
                                        borderRadius: 1,
                                    }}
                                    hoverScrollbar={true}
                                    scrollbarHeight="8px"
                                    scrollbarWidth="8px"
                                    scrollbarThumbColor="#c1c1c1"
                                    scrollbarHoverColor="#a8a8a8"
                                >
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow className="tr">
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Lokasi
                                                </TableCell>
                                                {/* Tambahan: Status Lokasi */}
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Status Lokasi
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Rencana Yield (Ton/Ha)
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Yield (Ton/Ha)
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Tanggal Closing
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dataList.map((item, index) => (
                                                <TableRow key={`${item.firebaseKey}-${index}`} hover>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Box>
                                                            <div style={{ fontWeight: "500" }}>{item.lokasi}</div>
                                                            <div style={{ fontSize: "0.75em", color: "#666" }}>
                                                                ID: {item.id_lokasi || "-"}
                                                            </div>
                                                        </Box>
                                                    </TableCell>
                                                    {/* Tambahan: Cell Status Lokasi */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {item.status || "-"}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {item.rencana_yield}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {item.yield}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {item.tanggal_closing}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CustomTableContainer>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Menu Dropdown Bulan */}
                <Menu
                    anchorEl={monthAnchorEl}
                    open={Boolean(monthAnchorEl)}
                    onClose={handleMonthClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {bulanList.map((bulan) => (
                        <MenuItem
                            key={bulan.id}
                            onClick={() => handleMonthSelect(bulan.id)}
                            selected={selectedMonth === bulan.id}
                        >
                            {bulan.nama}
                        </MenuItem>
                    ))}
                </Menu>

                {/* Menu Dropdown Tahun */}
                <Menu
                    anchorEl={yearAnchorEl}
                    open={Boolean(yearAnchorEl)}
                    onClose={handleYearClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {yearList.map((year) => (
                        <MenuItem key={year} onClick={() => handleYearSelect(year)} selected={selectedYear === year}>
                            {year}
                        </MenuItem>
                    ))}
                </Menu>
            </Paper>
        </Box>
    );
}

export default LokasiClosingByMouth;